import { useEffect, useState } from "react";

import { PatientKind } from "common/types";
import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Accordion from "client/common/components/interactive/Accordion";

import { SelectedProductFilters, ProductSearchFilters } from "../types";
import SearchFilter from "./SearchFilter";

type ProductSearchFilterModalProps = {
  options: ProductSearchFilters;
  isVisible: boolean;
  onClose: () => void;
  value: SelectedProductFilters;
  onChange: (search: SelectedProductFilters) => void;
};

const ProductSearchFilterModal = ({
  options,
  isVisible,
  onClose,
  value,
  onChange,
}: ProductSearchFilterModalProps) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // We want to ensure the params are up to date when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetSearch = () => {
    setUpdatedFilters({
      category_ids: [],
      manufacturer_ids: [],
      species: [],
      incentive_ids: [],
      rebate_ids: [],
      prescription_required: [],
    });
  };

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  const filterItems = [
    {
      key: "species",
      options: options.species,
      label: "Species",
      value: updatedFilters.species,
      content: (
        <SearchFilter<PatientKind>
          options={options.species}
          value={updatedFilters.species}
          onChange={(species) =>
            setUpdatedFilters({
              ...updatedFilters,
              species,
            })
          }
        />
      ),
    },
    {
      key: "category",
      options: options.categories,
      label: "Categories",
      value: updatedFilters.category_ids,
      content: (
        <SearchFilter
          options={options.categories}
          value={updatedFilters.category_ids}
          onChange={(category_ids) => setUpdatedFilters({ ...updatedFilters, category_ids })}
        />
      ),
    },
    {
      key: "manufacturer",
      options: options.manufacturers,
      label: "Manufacturers",
      value: updatedFilters.manufacturer_ids,
      content: (
        <SearchFilter
          options={options.manufacturers}
          value={updatedFilters.manufacturer_ids}
          onChange={(manufacturer_ids) =>
            setUpdatedFilters({ ...updatedFilters, manufacturer_ids })
          }
        />
      ),
    },
    {
      key: "prescription_required",
      options: options.prescription_required,
      label: "Type",
      value: updatedFilters.prescription_required,
      content: (
        <SearchFilter
          options={options.prescription_required}
          value={updatedFilters.prescription_required}
          onChange={(prescription_required) =>
            setUpdatedFilters({ ...updatedFilters, prescription_required })
          }
        />
      ),
    },
  ];

  // Every dimension counts, hidden ones included: Reset is the only way to clear a value whose
  // dimension came back with no options.
  const filterCount = filterItems.flatMap<number | string | boolean>((item) => item.value).length;

  const accordionItems = filterItems.filter((item) => item.options.length);

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <Accordion.Multi
            items={accordionItems}
            initialOpenKeys={accordionItems.map((item) => item.key)}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetSearch}
            disabled={!filterCount}
          >
            Reset {toCount(filterCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default ProductSearchFilterModal;
